import { observer } from "mobx-react";
import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { UserAccountType } from "@common/constants";
import {
	addCanBeSelected,
	SelectableWallets,
} from "@helpers/shared/addPropertyToWallet";
import { getWalletTranslationKey } from "@utils";
import { useRootAppStore } from "@hooks";
import { THEME_SETTINGS } from "@theme-settings";
import { useAppContext } from "@state/hooks/shared";
import { redirect } from "@state";

export default observer(function UserBalanceDisplay(props: {
	isSlim?: boolean;
	isInMenu?: boolean;
}) {
	const { t } = useTranslation("account-settings-navigation");
	const rootStore = useRootAppStore();
	const casinoSearchStore = rootStore.casinoSearchStore;
	const { culture } = useAppContext();

	if (rootStore.userAuthStore.userAccounts == null) {
		return null;
	}
	const numberOfWallets = rootStore.userAuthStore.userAccounts.length;
	const shouldShowAccountSelector = true;

	const handleClick = () => {
		if (
			casinoSearchStore &&
			casinoSearchStore.casinoStore &&
			window.location.pathname.includes("games")
		) {
			casinoSearchStore.casinoStore.openTransferFundsMenu();
		} else {
			redirect(`/${culture}/account-settings`);
		}
	};

	if (props.isSlim) {
		return (
			<>
				{rootStore.userAuthStore.userAccounts.map((ua) => {
					if (
						ua.accountTypeId ===
						rootStore.userAuthStore.activeAccountTypeId
					) {
						const balance =
							ua.balance.toFixed(2) +
							" " +
							ua.currency.displayCode;

						return (
							<button
								key={ua.abrv}
								className={classNames(
									"btn--split__label btn--reset",
									{
										"is-single": numberOfWallets <= 1,
										"is-multi": numberOfWallets > 1,
									}
								)}
								onClick={handleClick}
								style={{ cursor: "pointer" }}
							>
								<span className="btn--split__clip">
									{balance}
								</span>
							</button>
						);
					}
				})}
			</>
		);
	}
	if (numberOfWallets == 1) {
		return (
			<li>
				<label
					htmlFor={`${rootStore.userAuthStore.userAccounts[0].id}${
						props.isInMenu ? "-menu" : ""
					}`}
					className="sidebar__balance__label"
				></label>
				<span className="sidebar__balance__type u-mar--left--reset">
					{t<string>(`account-settings-navigation:ACCOUNT_BALANCE`)}:
				</span>
				<span className="sidebar__balance__amount">
					<span className="sidebar__balance__value">
						{rootStore.userAuthStore.userAccounts[0].balance.toFixed(
							2
						)}
					</span>
					<span className="sidebar__balance__currency">
						{
							rootStore.userAuthStore.userAccounts[0].currency
								.displayCode
						}
					</span>
				</span>
			</li>
		);
	}
	const userWalletsMapped = addCanBeSelected(
		rootStore.userAuthStore.userAccounts,
		rootStore.userAuthStore
	);
	return (
		<>
			{userWalletsMapped.map((ua) => {
				if (
					THEME_SETTINGS.isDKMW &&
					ua.abrv === UserAccountType.SportBettingAccountShopOnline
				) {
					return null;
				}
				const walletName = t<string>(getWalletTranslationKey(ua.abrv));

				const itemClasses = classNames(
					"wallet__item input--radio input--radio--sml",
					{
						[`wallet__item--${ua.abrv}`]: shouldShowAccountSelector,
					}
				);

				const labelClasses = classNames(
					"wallet__account input--radio__label input--radio--sml__label",
					[`wallet__account--${ua.abrv}`],
					{
						"wallet__account--col": props.isInMenu,
					}
				);

				const iconClasses = classNames([
					`wallet__icon u-icon u-icon--sml u-icon--wallet u-icon--wallet--${ua.abrv}`,
				]);

				const balanceClasses = classNames("wallet__balance", {
					"wallet__balance--menu wallet__balance--full":
						props.isInMenu,
				});

				return (
					<li key={ua.abrv} className={itemClasses}>
						<AccountTypeSelector
							userAccount={ua}
							isInMenu={props.isInMenu}
							shouldShowAccountSelector={
								shouldShowAccountSelector
							}
						/>
						<label
							htmlFor={`${ua.id}${props.isInMenu ? "-menu" : ""}`}
							className={labelClasses}
						>
							<span className="wallet__type">
								<i className={iconClasses} />
								<span className="wallet__name">
									{walletName}:
								</span>
							</span>
							<span className={balanceClasses}>
								<span className="wallet__amount">
									{ua.balance.toFixed(2)}
								</span>
								<span className="wallet__currency">
									{ua.currency.displayCode}
								</span>
							</span>
						</label>
					</li>
				);
			})}
		</>
	);
});

const AccountTypeSelector = observer(function AccountTypeSelector(props: {
	userAccount: SelectableWallets;
	shouldShowAccountSelector?: boolean;
	isInMenu?: boolean;
}) {
	const rootStore = useRootAppStore();

	if (
		("canBeSelected" in props.userAccount &&
			props.userAccount.canBeSelected == false) ||
		!props.shouldShowAccountSelector
	) {
		return (
			<input
				id={`${props.userAccount.id}${props.isInMenu ? "-menu" : ""}`}
				name={`accountSelector${props.isInMenu ? "-menu" : ""}`}
				className="input--radio__box input--radio--sml__box is-hidden"
				type="radio"
				disabled={true}
			/>
		);
	}

	return (
		<input
			id={`${props.userAccount.id}${props.isInMenu ? "-menu" : ""}`}
			name={`accountSelector${props.isInMenu ? "-menu" : ""}`}
			className="input--radio__box input--radio--sml__box"
			type="radio"
			value={props.userAccount.accountTypeId}
			onChange={() => {
				rootStore.userAuthStore.setActiveUserAccountTypeId(
					props.userAccount.accountTypeId
				);

				if (
					props.userAccount.abrv ==
					"sport-betting-account-shop-online"
				) {
					rootStore.waitingPeriodStore.hidePopup();
				}
			}}
			checked={
				rootStore.userAuthStore.activeAccountTypeId ===
				props.userAccount.accountTypeId
			}
		/>
	);
});
